import { useEffect, useState } from "react";
import { BarChart3, Plus, Trash2, Power, Trophy, ShoppingBag } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import ImageUpload from "@/components/ImageUpload";

const inputCls = "w-full rounded-lg border border-input bg-[#060B18] px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500";
const EMPTY_OPT = { label: "", image_url: "" };

export default function PollsTab() {
  const [polls, setPolls] = useState([]);
  const [question, setQuestion] = useState("");
  const [options, setOptions] = useState([{ ...EMPTY_OPT }, { ...EMPTY_OPT }]);

  const load = () => api.get("/admin/polls").then((r) => setPolls(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  const setOpt = (i, patch) => setOptions(options.map((o, j) => (j === i ? { ...o, ...patch } : o)));

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/polls", { question, options, is_active: true });
      toast.success("Sondage publié sur la page d'accueil");
      setQuestion("");
      setOptions([{ ...EMPTY_OPT }, { ...EMPTY_OPT }]);
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const toggle = async (p) => {
    if (p.is_active && !window.confirm(`Clore le sondage « ${p.question} » ?${p.total_votes > 0 ? " Les abonnés newsletter recevront le résultat par email." : ""}`)) return;
    try {
      const { data } = await api.put(`/admin/polls/${p.id}`, { question: p.question, options: p.options, is_active: !p.is_active });
      if (p.is_active && data.notified > 0) {
        toast.success(`Sondage clos — ${data.notified} abonné(s) notifié(s) du résultat par email`);
      } else {
        toast.success(p.is_active ? "Sondage clos" : "Sondage activé (les autres sont clos)");
      }
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Supprimer le sondage « ${p.question} » et ses votes ?`)) return;
    try {
      await api.delete(`/admin/polls/${p.id}`);
      toast.success("Sondage supprimé");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const toProduct = async (p) => {
    try {
      const { data } = await api.post(`/admin/polls/${p.id}/to-product`);
      toast.success(`Produit « ${data.product.name} » créé avec description rédigée par l'IA (${data.percent}% des votes) — définissez prix et stock dans Produits & Stocks`);
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="grid gap-6" data-testid="polls-admin">
      <form onSubmit={create} className="card-tactical grid gap-4 p-5" data-testid="admin-poll-form">
        <p className="flex items-center gap-2 font-heading text-lg font-bold uppercase">
          <Plus className="h-5 w-5 text-orange-500" /> Nouveau sondage communauté
        </p>
        <p className="text-xs text-slate-500">
          Le sondage créé devient actif immédiatement (un seul à la fois) et apparaît sur la page d'accueil.
          Un visiteur = un vote. Chaque option peut avoir une image (téléversée ou générée par IA).
        </p>
        <input placeholder="Question (ex : Quel design pour le prochain maillot ?)" value={question}
          data-testid="admin-poll-question" onChange={(e) => setQuestion(e.target.value)} className={inputCls} required />
        <div className="grid gap-3">
          {options.map((opt, i) => (
            <div key={i} className="rounded-lg border border-slate-700/60 p-3" data-testid={`admin-poll-option-block-${i}`}>
              <div className="flex gap-2">
                <input placeholder={`Option ${i + 1}`} value={opt.label} data-testid={`admin-poll-option-${i}`}
                  onChange={(e) => setOpt(i, { label: e.target.value })} className={inputCls} required={i < 2} />
                {options.length > 2 && (
                  <button type="button" onClick={() => setOptions(options.filter((_, j) => j !== i))} data-testid={`admin-poll-option-remove-${i}`}
                    className="rounded-lg border border-red-500/40 px-3 text-red-400 hover:bg-red-500/10"><Trash2 className="h-4 w-4" /></button>
                )}
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <ImageUpload testid={`admin-poll-option-upload-${i}`} onUploaded={(url) => setOpt(i, { image_url: url })} />
                {opt.image_url && (
                  <>
                    <img src={opt.image_url} alt="" data-testid={`admin-poll-option-img-${i}`}
                      className="h-12 w-12 rounded-lg border border-orange-500/20 object-cover" />
                    <button type="button" onClick={() => setOpt(i, { image_url: "" })} data-testid={`admin-poll-option-img-del-${i}`}
                      className="text-xs font-bold uppercase text-red-400 hover:text-red-300">Retirer</button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
        {options.length < 6 && (
          <button type="button" onClick={() => setOptions([...options, { ...EMPTY_OPT }])} data-testid="admin-poll-add-option"
            className="w-fit rounded-lg border border-slate-600 px-4 py-2 text-xs font-bold uppercase text-slate-300 hover:border-orange-500 hover:text-orange-400">
            + Ajouter une option
          </button>
        )}
        <button type="submit" data-testid="admin-poll-submit"
          className="w-fit rounded-lg bg-orange-500 px-5 py-2.5 text-sm font-bold uppercase text-[#060B18] transition-all hover:scale-[1.02] hover:bg-orange-400">
          Publier le sondage
        </button>
      </form>

      <div className="grid gap-3">
        {polls.length === 0 && <p className="text-sm text-slate-500">Aucun sondage. Créez-en un pour le voir apparaître sur l'accueil.</p>}
        {polls.map((p) => (
          <div key={p.id} data-testid={`admin-poll-row-${p.id}`} className="card-tactical p-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                {p.is_active ? (
                  <span className="mr-2 rounded bg-orange-500 px-2 py-0.5 text-[11px] font-black uppercase text-[#060B18]">En cours</span>
                ) : (
                  <span className="mr-2 rounded border border-slate-500/60 bg-slate-500/15 px-2 py-0.5 text-[11px] font-bold uppercase text-slate-300">Clos</span>
                )}
                <span className="font-heading text-lg font-bold uppercase">{p.question}</span>
                <span className="ml-2 text-sm text-slate-400">{p.total_votes} vote{p.total_votes > 1 ? "s" : ""}</span>
              </div>
              <div className="flex gap-2">
                <button onClick={() => toggle(p)} data-testid={`admin-poll-toggle-${p.id}`}
                  title={p.is_active ? "Clore le sondage" : "Activer le sondage"}
                  className="rounded-lg border border-orange-500/40 p-2 text-orange-400 hover:bg-orange-500/10"><Power className="h-4 w-4" /></button>
                <button onClick={() => remove(p)} data-testid={`admin-poll-delete-${p.id}`}
                  className="rounded-lg border border-red-500/40 p-2 text-red-400 hover:bg-red-500/10"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
            <div className="mt-3 grid gap-2">
              {p.options.map((o, i) => {
                const count = (p.counts || [])[i] || 0;
                const pct = p.total_votes > 0 ? Math.round((count / p.total_votes) * 100) : 0;
                const isWinner = p.total_votes > 0 && count === Math.max(...(p.counts || [0]));
                return (
                  <div key={i} className={`relative flex items-center gap-2 overflow-hidden rounded-md border px-2 py-1.5 text-xs ${isWinner ? "border-orange-500/60" : "border-slate-700"} bg-[#060B18]`}
                    data-testid={`admin-poll-optstat-${p.id}-${i}`}>
                    <div className="absolute inset-y-0 left-0 bg-orange-500/15" style={{ width: `${pct}%` }} />
                    {o.image_url && <img src={o.image_url} alt="" className="relative h-8 w-8 shrink-0 rounded object-cover" />}
                    <span className="relative flex-1 text-slate-200">
                      {isWinner && p.total_votes > 0 && <Trophy className="mr-1 inline h-3.5 w-3.5 text-orange-400" />}
                      {o.label}
                    </span>
                    <span className="relative font-bold text-orange-400">{count} ({pct}%)</span>
                  </div>
                );
              })}
            </div>
            {p.total_votes > 0 && (
              <button onClick={() => toProduct(p)} data-testid={`admin-poll-to-product-${p.id}`}
                className="mt-3 flex w-fit items-center gap-2 rounded-lg bg-emerald-500 px-4 py-2 text-xs font-bold uppercase text-[#060B18] transition-all hover:scale-[1.02] hover:bg-emerald-400">
                <ShoppingBag className="h-4 w-4" /> Créer le produit gagnant
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
